import React, { createContext, useContext, useState, useEffect } from "react";

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      "useLocalization must be used within a LocalizationProvider"
    );
  }
  return context;
};

// Translation files
const translations = {
  en: {
    // Common
    question: "Question",
    outOf: "out of",
    years: "years",
    next: "Next",
    previous: "Previous",
    complete: "Complete",
    submitting: "Submitting...",
    male: "Male",
    female: "Female",
    welcome: "Welcome",
    loading: "Loading...",
    error: "Error",
    retry: "Retry",
    back: "Back",
    submit: "Submit",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    loadingAssessments: "Loading assessments...",

    // Dashboard
    dashboard: {
      welcomeMessage: "Welcome, {name}!",
      checkVitals: "Check Your Vitals",
      joinConsultation: "Join Online Consultation",
      healthAssessment: "Health Risk Assessment",
      chatManager: "Chat with Case Manager",
      askAI: "Ask AI",
      knowledgeBank: "PHM Knowledge Bank",
      endSession: "End session",
    },

    // Welcome Screen
    welcomeScreen: {
      title: "Welcome to the Future of Wellbeing",
      subtitle: "Your health companion",
      getStarted: "Start Your Journey",
    },

    // Vitals Check
    vitalsCheck: {
      title: "Vitals Check",
      instructions: "Please follow the instructions to check your vitals",
      startCheck: "Start Vitals Check",
      proceedToAssessments: "Proceed to assessments",
      vitals: {
        muscleMass: "Muscle Mass",
        weight: "Weight",
        boneMass: "Bone Mass",
        heartPulse: "Heart Pulse",
        fatPercentage: "Fat Percentage",
        bloodPressure: "Blood Pressure",
        bmi: "BMI",
        metabolicAge: "Metabolic Age",
        nerveHealth: "Nerve Health",
        fatRatio: "Fat Ratio",
        hydration: "Hydration",
        height: "Height",
        heartRate: "Heart Rate",
        spo2: "SPO2",
        temperature: "Temperature",
      },
    },

    // Consultation
    consultation: {
      title: "Online Consultation",
      joinCall: "Join Call",
      waitingRoom: "Waiting Room",
      preparing: "Preparing your consultation...",
      connectionError:
        "Unable to connect. Please try again or contact support.",
      retry: "Retry",
      endMeeting: "End Meeting",
      endingMeeting: "Ending meeting...",
      notAvailable: "Consultation Not Available",
      notAvailableMessage: "Online consultation is not available at this time. Please check the schedule and try again during available hours.",
      checkingAvailability: "Checking availability...",
    },

    // Meeting SDK
    meetingSDK: {
      callError: "Call Error",
      joiningCall: "Joining call...",
      retry: "Retry",
      errorOccurred: "An error occurred during the call",
      failedToJoin: "Failed to join the call",
      connectingToDoctor:
        "We're connecting you to your doctor — your consultation will begin shortly.",
    },

    // Assessment
    assessment: {
      title: "Health Assessment",
      description: "Your answers help your doctor tailor your consultation",
      skipAll: "Skip all",
      talkToDoctor: "Talk to a doctor",
      questions: "questions",
    },

    // End Session
    endSession: {
      title: "You've successfully completed your pod session",
      description:
        "Your measurements report and medical profile link have been sent to your email.",
      endButton: "End Session",
      confirmEnd: "Are you sure you want to end this session?",
      sessionSummary: "Session Summary",
    },

    // AI Assistant
    aiAssistant: {
      title: "AI Assistant",
      instruction:
        "Scan the QR code and we'll connect you with one of our case managers",
      repeatInstructions: "Repeat Instructions",
      skip: "Skip",
      starting: "Starting...",
      start: "Start",
      clickAgainToStop: "Click again to stop",
      talkToAI: "Ask AI",
      chooseLanguage: "Choose AI Assistant Language:",
      chooseGender: "Choose Gender:",
      listening: "I'm listening...",
    },

    // Live Avatar
    liveAvatar: {
      chat: "Chat",
      speak: "Speak",
      start: "Start",
      stop: "Stop",
      listening: "Listening...",
      connectingToAvatar: "Connecting to avatar...",
      connected: "Connected",
      disconnected: "Disconnected",
      typeMessage: "Type your message...",
      send: "Send",
      retry: "Retry",
      close: "Close",
      failedToConnect: "Failed to connect to avatar. Please try again.",
      selectAvatarLanguage: "Select Avatar Language",
      avatarWillSpeak: "The avatar will speak in:",
      micPermissionMessage: "Please enable microphone access in your browser settings",
    },

    // Language Toggle
    language: {
      english: "English",
      arabic: "Arabic",
      selectLanguage: "Select Language",
    },
  },

  ar: {
    // Common
    question: "سؤال",
    outOf: "من",
    years: "سنة",
    complete: "إنهاء",
    submitting: "جاري التسليم...",
    male: "ذكر",
    female: "أنثى",
    welcome: "مرحباً",
    loading: "جاري التحميل...",
    error: "خطأ",
    retry: "إعادة المحاولة",
    back: "رجوع",
    next: "التالي",
    previous: "السابق",
    submit: "إرسال",
    cancel: "إلغاء",
    confirm: "تأكيد",
    close: "إغلاق",
    loadingAssessments: "جاري تحميل التقييمات...",
    // Dashboard
    dashboard: {
      welcomeMessage: "مرحباً، {name}!",
      checkVitals: "فحص العلامات الحيوية",
      joinConsultation: "انضم للاستشارة عبر الإنترنت",
      healthAssessment: "تقييم المخاطر الصحية",
      chatManager: "تحدث مع مدير الحالة",
      askAI: "اسأل المساعد",
      knowledgeBank: "بنك المعرفة الصحي",
      endSession: "إنهاء الجلسة",
    },

    // Welcome Screen
    welcomeScreen: {
      title: "مرحباً بك في مستقبل الرفاهية",
      subtitle: "رفيقك الصحي",
      getStarted: "ابدأ رحلتك",
    },

    // Vitals Check
    vitalsCheck: {
      title: "فحص العلامات الحيوية",
      instructions: "يرجى اتباع التعليمات لفحص العلامات الحيوية",
      startCheck: "بدء فحص العلامات الحيوية",
      proceedToAssessments: "المتابعة إلى التقييمات",
      vitals: {
        muscleMass: "كتلة العضلات",
        weight: "الوزن",
        boneMass: "كتلة العظام",
        heartPulse: "نبض القلب",
        fatPercentage: "نسبة الدهون",
        bloodPressure: "ضغط الدم",
        bmi: "مؤشر كتلة الجسم",
        metabolicAge: "العمر الأيضي",
        nerveHealth: "صحة الأعصاب",
        fatRatio: "نسبة الدهون",
        hydration: "الترطيب",
        height: "الطول",
        heartRate: "معدل ضربات القلب",
        spo2: "SPO2",
        temperature: "درجة الحرارة",
      },
    },

    // Consultation
    consultation: {
      title: "الاستشارة عبر الإنترنت",
      joinCall: "انضم للمكالمة",
      waitingRoom: "غرفة الانتظار",
      preparing: "جاري تحضير استشارتك...",
      connectionError:
        "غير قادر على الاتصال. يرجى المحاولة مرة أخرى أو الاتصال بالدعم.",
      retry: "إعادة المحاولة",
      endMeeting: "إنهاء المكالمة",
      endingMeeting: "جاري إنهاء المكالمة...",
      notAvailable: "الاستشارة غير متاحة",
      notAvailableMessage: "الاستشارة عبر الإنترنت غير متاحة في هذا الوقت. يرجى التحقق من الجدول الزمني والمحاولة مرة أخرى خلال ساعات العمل المتاحة.",
      checkingAvailability: "جاري التحقق من التوفر...",
    },

    // Meeting SDK
    meetingSDK: {
      callError: "خطأ في المكالمة",
      joiningCall: "جاري الانضمام للمكالمة...",
      retry: "إعادة المحاولة",
      errorOccurred: "حدث خطأ أثناء المكالمة",
      failedToJoin: "فشل في الانضمام للمكالمة",
      connectingToDoctor: "نحن نوصلك بطبيبك — ستبدأ استشارتك قريباً.",
    },

    // Assessment
    assessment: {
      title: "التقييم الصحي",
      description: "تساعد إجاباتك على تخصيص استشارتك للطبيب",
      skipAll: "تخطي الكل",
      talkToDoctor: "تحدث مع الطبيب",
      questions: "أسئلة",
    },

    // End Session
    endSession: {
      title: "لقد أكملت جلسة البود بنجاح",
      description:
        "تم إرسال تقرير القياسات وروابط الملف الطبي إلى بريدك الإلكتروني.",
      endButton: "إنهاء الجلسة",
      confirmEnd: "هل أنت متأكد من إنهاء هذه الجلسة؟",
      sessionSummary: "ملخص الجلسة",
    },

    // AI Assistant
    aiAssistant: {
      title: "تحدث مع مدير الحالة",
      instruction:
        "امسح رمز الاستجابة السريعة وسنقوم بتوصيلك بأحد مديري الحالة لدينا",
      repeatInstructions: "تكرار التعليمات",
      skip: "تخطي",
      starting: "جاري البدء...",
      start: "ابدأ",
      clickAgainToStop: "انقر مرة أخرى للإيقاف",
      talkToAI: "اسأل المساعد",
      chooseLanguage: "اختر لغة المساعد:",
      chooseGender: "اختر الجنس:",
      listening: "أنا أستمع...",
    },

    // Live Avatar
    liveAvatar: {
      chat: "محادثة",
      speak: "تحدث",
      start: "ابدأ",
      stop: "توقف",
      listening: "جاري الاستماع...",
      connectingToAvatar: "جاري الاتصال بالمساعد...",
      connected: "متصل",
      disconnected: "غير متصل",
      typeMessage: "اكتب رسالتك...",
      send: "إرسال",
      retry: "إعادة المحاولة",
      close: "إغلاق",
      failedToConnect: "فشل الاتصال بالمساعد. يرجى المحاولة مرة أخرى.",
      selectAvatarLanguage: "اختر لغة المساعد",
      avatarWillSpeak: "سيتحدث المساعد باللغة:",
      micPermissionMessage: "يرجى تمكين الوصول إلى الميكروفون في إعدادات المتصفح",
    },

    // Language Toggle
    language: {
      english: "الإنجليزية",
      arabic: "العربية",
      selectLanguage: "اختر اللغة",
    },
  },
};

export const LocalizationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'en'
    return localStorage.getItem("app-language") || "en";
  });

  const [isRTL, setIsRTL] = useState(language === "ar");

  // Update RTL state when language changes
  useEffect(() => {
    setIsRTL(language === "ar");
    localStorage.setItem("app-language", language);

    // Update document direction
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, params = {}) => {
    const keys = key.split(".");
    let translation = translations[language];

    for (const k of keys) {
      translation = translation?.[k];
    }

    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    // Replace parameters in translation
    return translation.replace(
      /\{(\w+)\}/g,
      (match, param) => params[param] || match
    );
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "ar" : "en"));
  };

  const value = {
    language,
    isRTL,
    t,
    toggleLanguage,
    setLanguage,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
